import { useState } from 'react';
import {
  Box,
  Text,
  Table,
  Card,
  IconButton,
  Link,
} from '@nimbus-ds/components';
import { EditIcon, TrashIcon } from '@nimbus-ds/icons';
import { useTranslation } from 'react-i18next';
import { Domain } from '@tiendanube/common/src/enums';
import { Thumbnail } from '@tiendanube/components';
import { CustomHeaderCell } from 'commons/components';
import EditProductModal from './EditProductModal';
import type { ProductsFromImages } from '../../NewProductFromImagesPage';

interface ImageDragging {
  rowIndex: number;
  imageIndex: number;
}

interface ProductsIdentificationResultsTableProps {
  products: ProductsFromImages;
  isVisible: boolean;
  onProductsChange: (products: ProductsFromImages) => void;
}

function ProductsIdentificationResultsTable({
  products,
  isVisible,
  onProductsChange,
}: Readonly<ProductsIdentificationResultsTableProps>) {
  const { t } = useTranslation(Domain.CATALOG);
  const [editingIndex, setEditingIndex] = useState<number>();
  const [imageDragging, setImageDragging] = useState<ImageDragging>();
  const [rowOver, setRowOver] = useState<number>();

  const handleDeleteRow = (index: number) => {
    const updatedResults = products.filter((_, i) => i !== index);
    onProductsChange(updatedResults);
  };

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
  };

  const handleCloseEdit = () => {
    setEditingIndex(undefined);
  };

  if (!isVisible) {
    return null;
  }

  const columns = [
    {
      field: 'productName',
      headerName: t('products.fromImages.resultsTable.productName'),
      width: '48%',
    },
    {
      field: 'images',
      headerName: t('products.fromImages.resultsTable.images'),
      width: '44%',
    },
    {
      field: 'actions',
      headerName: t('products.fromImages.resultsTable.deleteRow'),
      width: '8%',
    },
  ];

  return (
    <>
      <Card>
        <Text fontSize="base" fontWeight="bold">
          {t('products.fromImages.resultsTable.title')}
        </Text>
        {products.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding="8"
          >
            <Text color="neutral-textLow">
              {t('products.fromImages.resultsTable.noResultsFound')}
            </Text>
          </Box>
        ) : (
          <Table>
            <Table.Head>
              {columns.map((column) => (
                <CustomHeaderCell key={column.field} width={column.width}>
                  {column.headerName}
                </CustomHeaderCell>
              ))}
            </Table.Head>
            <Table.Body>
              {products.map((row, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <Box display="flex" gap="2">
                      <Text fontWeight="bold" fontSize="base">
                        {row.name ||
                          t('products.fromImages.resultsTable.productName')}
                      </Text>
                      <Link
                        as="button"
                        onClick={() => handleEditClick(index)}
                        appearance="primary"
                        aria-label={t(
                          'products.fromImages.resultsTable.editProduct',
                        )}
                      >
                        <EditIcon />
                      </Link>
                    </Box>
                  </Table.Cell>
                  <Table.Cell>
                    <Box
                      display="grid"
                      gridTemplateColumns="1fr 1fr 1fr"
                      alignItems="center"
                      gap="1"
                      width="100%"
                      paddingTop="2"
                      paddingBottom="2"
                      minHeight="89px"
                      borderColor={
                        rowOver === index
                          ? 'primary-interactive'
                          : 'transparent'
                      }
                      borderWidth="2"
                      borderStyle="dotted"
                      borderRadius="1"
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                        setRowOver(index);
                      }}
                      onDragLeave={() => setRowOver(undefined)}
                      onDrop={() => {
                        if (!imageDragging) return;
                        const { rowIndex: fromRow, imageIndex: fromIdx } =
                          imageDragging;
                        if (fromRow === index) return;
                        const toRow = index;

                        // Add boundary checks
                        if (
                          !products[fromRow] ||
                          !products[fromRow].images[fromIdx]
                        ) {
                          setImageDragging(undefined);
                          setRowOver(undefined);
                          return;
                        }

                        const draggedImage = products[fromRow]?.images[fromIdx];
                        if (!draggedImage) return;

                        const updated = products.map((r, i) => {
                          if (i === fromRow) {
                            const imgs = r.images.slice();
                            imgs.splice(fromIdx, 1);
                            return { ...r, images: imgs };
                          }
                          if (i === toRow) {
                            return {
                              ...r,
                              images: [...r.images, draggedImage],
                            };
                          }
                          return r;
                        });

                        onProductsChange(updated);
                        setImageDragging(undefined);
                        setRowOver(undefined);
                      }}
                    >
                      {row.images.map((image, imageIndex) => (
                        <div
                          key={image.id}
                          draggable
                          onDragStart={() => {
                            setImageDragging({ rowIndex: index, imageIndex });
                          }}
                          onDragEnd={() => setImageDragging(undefined)}
                          style={{
                            cursor: 'grab',
                          }}
                          role="presentation"
                        >
                          <Thumbnail
                            key={image.id}
                            src={image.url}
                            width="96px"
                            aspectRatio="16-9"
                            altText={t(
                              'products.fromImages.resultsTable.altText',
                              {
                                imageNumber: imageIndex + 1,
                                productName: row.name,
                              },
                            )}
                          />
                        </div>
                      ))}
                    </Box>
                  </Table.Cell>
                  <Table.Cell>
                    <IconButton
                      source={<TrashIcon />}
                      size="2rem"
                      onClick={() => handleDeleteRow(index)}
                      aria-label={t(
                        'products.fromImages.resultsTable.deleteRow',
                      )}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Card>

      {editingIndex !== undefined && (
        <EditProductModal
          onDismiss={handleCloseEdit}
          product={products[editingIndex]}
          onConfirm={(updatedProduct) => {
            const updatedResults = products.map((product, i) =>
              i === editingIndex ? updatedProduct : product,
            );
            onProductsChange(updatedResults);
          }}
        />
      )}
    </>
  );
}

export default ProductsIdentificationResultsTable;
